import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

interface ButtonGroupOption {
	value: string;
	label: string;
	disabled?: boolean;
}

interface ButtonGroupProps {
	name: string;
	value?: string;
	onChange: (value: string) => void;
	options: ButtonGroupOption[];
	disabled?: boolean;
	className?: string;
	size?: 'sm' | 'md' | 'lg';
	fullWidth?: boolean;
	label?: string;
	error?: boolean | string;
	required?: boolean;
}

export function ButtonGroup({
	name,
	value,
	onChange,
	options,
	disabled = false,
	className,
	size = 'md',
	fullWidth = false,
	label,
	error,
	required,
}: ButtonGroupProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	const sizeConfig = {
		sm: 'px-2 py-1 text-xs',
		md: 'px-3 py-2 text-sm',
		lg: 'px-4 py-2.5 text-base',
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLButtonElement>,
		optionValue: string,
	) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			if (!disabled) {
				onChange(optionValue);
			}
		}
	};

	return (
		<div className={cn('min-w-0', className)}>
			{label && (
				<div
					className={cn('text-sm font-medium mb-2', colors.classes.textPrimary)}
				>
					{t(label)}
					{required && (
						<span className={cn('ml-1', colors.classes.textDanger)}>*</span>
					)}
				</div>
			)}

			<div
				className={cn('flex flex-wrap gap-2', fullWidth && 'w-full')}
				role="radiogroup"
				aria-required={required}
				aria-invalid={!!error}
			>
				{options.map((option) => {
					const isSelected = value === option.value;
					const isDisabled = disabled || option.disabled;

					return (
						<button
							key={option.value}
							type="button"
							role="radio"
							aria-checked={isSelected}
							aria-disabled={isDisabled}
							name={name}
							onClick={() => !isDisabled && onChange(option.value)}
							onKeyDown={(e) => handleKeyDown(e, option.value)}
							disabled={isDisabled}
							className={cn(
								'rounded-md border transition-colors',
								sizeConfig[size],
								fullWidth && 'flex-1',
								isSelected
									? cn(
											colors.classes.borderPrimary,
											colors.classes.controlSelected,
											'font-semibold',
										)
									: cn(
											colors.classes.borderDefault,
											colors.classes.bgPrimary,
											colors.classes.textSecondary,
											!isDisabled && 'hover:bg-canvas',
										),
								isDisabled && 'cursor-not-allowed opacity-50',
							)}
						>
							{t(option.label)}
						</button>
					);
				})}
			</div>

			{error && typeof error === 'string' && (
				<p className={cn('mt-2 text-sm', colors.classes.textDanger)}>
					{t(error)}
				</p>
			)}
		</div>
	);
}
